// ChatWindow.jsx
import { useState, useEffect, useRef } from "react";
import { SendHorizonal } from "lucide-react";
import { db, doc, updateDoc } from "./firebase";
import responderPorCanal from "./utils/responderPorCanal";
import ChatBubble from "./components/ChatBubble";

function toMillis(ts) {
    if (typeof ts === "number") return ts;
    if (ts?.toMillis) return ts.toMillis();
    if (ts?.seconds) return ts.seconds * 1000;
    return 0;
}

export default function ChatWindow({ conversation, collectionName }) {
    const {
        id,
        personFullName,
        personId,
        channel,
        historyChat = [],
        lastUpdate,
        agentControl,
        senderProfile, // opcional: si ya lo guardas en Firestore, tiene prioridad
    } = conversation;

    const [text, setText] = useState("");
    const bottomRef = useRef(null);

    // Ventana 24 h
    const lastMs = toMillis(lastUpdate);
    const canSend = Date.now() - lastMs < 86_400_000;

    // Derivar el perfil por colección si no viene desde el doc
    const derivedProfile =
        senderProfile ||
        (collectionName === "activeUsersConsumidorFinal" ? "consumidor" : "mayorista");

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [historyChat]);

    const send = async () => {
        const trimmed = text.trim();
        if (!trimmed || !agentControl || !canSend) return;

        const newMsg = { agent: trimmed, timestamp: Date.now() };

        try {
            // 1) Persistir en la colección correcta
            await updateDoc(doc(db, collectionName, id), {
                historyChat: [...historyChat, newMsg],
                lastUpdate: newMsg.timestamp,
            });

            // 2) Enviar por el canal con el perfil/salida correcto
            const res = await responderPorCanal(channel, personId, trimmed, {
                profile: derivedProfile,
            });

            if (!res.ok) {
                const errTxt = await res.text().catch(() => "");
                console.error("Fallo envío canal:", res.status, errTxt);
            }

            setText("");
        } catch (err) {
            console.error("Error al enviar:", err);
        }
    };

    return (
        <div className="card h-full bg-base-100">
            {/* Cabecera */}
            <div className="stats w-full rounded-none border-b">
                <div className="stat">
                    <div className="stat-title">Contacto</div>
                    <div className="stat-value text-lg">{personFullName}</div>
                    <div className="stat-desc">{channel}</div>
                </div>
                <div className="stat">
                    <div className="stat-title">Control</div>
                    <div className={`stat-value ${agentControl ? "text-success" : "text-secondary"}`}>
                        {agentControl ? "Agente" : "Bot"}
                    </div>
                    <div className="stat-desc">
                        Último {lastMs ? new Date(lastMs).toLocaleTimeString() : "—"}
                    </div>
                </div>
            </div>

            {/* Historial */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {historyChat.map((m, i) => (
                    <ChatBubble key={i} msg={m} />
                ))}
                <div ref={bottomRef} />
            </div>

            {/* Composer */}
            <div className="border-t p-4 bg-base-200">
                {agentControl ? (
                    <>
                        {!canSend && (
                            <p className="text-center text-sm text-error mb-2">
                                Pasaron 24 h — no se puede enviar.
                            </p>
                        )}
                        <div className="flex gap-2">
                            <textarea
                                className="textarea textarea-bordered flex-1 resize-none h-12"
                                placeholder={`Escribe un mensaje… (salida: ${derivedProfile})`}
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                disabled={!canSend}
                            />
                            <button
                                className="btn btn-primary btn-square"
                                onClick={send}
                                disabled={!text.trim() || !canSend}
                                title={`Guardar en ${collectionName} y enviar por ${derivedProfile}`}
                            >
                                <SendHorizonal size={18} />
                            </button>
                        </div>
                    </>
                ) : (
                    <p className="text-center text-sm text-gray-500">
                        El bot está en control. Toma control para escribir.
                    </p>
                )}
            </div>
        </div>
    );
}
